/* eslint-disable react-hooks/rules-of-hooks */
import styled from '@emotion/styled';
import {
  Avatar,
  Button,
  Icon,
  IconButton,
  InfoColumnIcon,
  Input,
  Popover,
  Skeleton,
  ToggleButton,
  Tooltip,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { dates, Stage } from '@innovamat/radiance-utils';
import type { TFunction } from 'i18next';
import type { HTMLInputTypeAttribute } from 'react';
import {
  ParentsStatusCell,
  StudentStatusCell,
} from '../../../components/common-table-components';
import type { ClassroomCapabilities } from '../../../hooks/use-classroom-capabilities';
import { DigitalPracticeSublabelContent } from '../components/digital-practice-sublabel-content';

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  pointer-events: all !important;
  width: 100%;
`;

const SublabelFlexContainer = styled(FlexContainer)`
  align-items: flex-end;
  line-height: 14px;
`;

const WeeklyTimeContainer = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  word-break: break-word;

  .info-icon {
    margin-left: 6px;
    display: inline-flex;
    transform: translateY(2px);
  }
`;

const StyledSpan = styled.span<{
  width?: number | string;
  notVisible?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
  width: ${({ width }) =>
    (typeof width === 'number' ? `${width}px` : width) || 'auto'};
  max-width: ${({ width }) =>
    typeof width === 'number' ? `${width}px` : width || 'none'};
  min-width: ${({ width }) =>
    typeof width === 'number' ? `${width}px` : width || 'auto'};
  visibility: ${({ notVisible }) => (notVisible ? 'hidden' : '')};
`;

const SublabelStyledSpan = styled(StyledSpan)`
  align-items: flex-start;
`;

const StyledInput = styled(Input)`
  width: 100%;

  /* Para WebKit (Chrome, Safari, Edge) */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Para Firefox */
  &::-moz-inner-spin-button,
  &::-moz-outer-spin-button {
    -moz-appearance: none;
    margin: 0;
  }
`;

const EmailField = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTooltip = styled(Tooltip)`
  min-width: 0;
`;

const SubtleText = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const DigitalPracticeSublabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;

  span {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
    hyphens: auto;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

type Translate = TFunction<'translation', undefined, 'translation'>;
type Option = { value: string | number; label: string };

export type Parent = {
  id: string;
  userInfo: {
    email: string;
    lastLogin: string;
    firstName: string;
    lastName: string;
    token: string;
  };
};

const TEMPLATE_ID = 'template';

const columnWidth = {
  15: '15%',
  20: '20%',
  25: '25%',
  30: '30%',
  33: '33%',
  40: '40%',
  50: '50%',
};

const DropDownComponent = ({
  label,
  options,
  handleInputChange,
  isDisabled,
  inputKey,
  studentId,
}: {
  label: string;
  options: Option[];
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  studentId: string;
  isDisabled?: boolean;
  inputKey: string;
}): JSX.Element => {
  return (
    <Popover
      style={{ overflow: 'hidden' }}
      trigger={
        <Button
          size={'M'}
          variant={'tertiary'}
          rightIcon="ExpandMoreIcon"
          disabled={isDisabled}
          oneLine
          fill
          className="table-button"
        >
          {label}
        </Button>
      }
    >
      {options.map((option) => (
        <Popover.Item
          key={option.value}
          onSelectItem={() =>
            handleInputChange(inputKey, option.value, studentId)
          }
          state={label === option.label ? 'selected' : 'active'}
        >
          {option.label}
        </Popover.Item>
      ))}
    </Popover>
  );
};

type ButtonInputComponentProps = {
  type?: HTMLInputTypeAttribute;
  studentId: string;
  inputKey: string;
  value: string | number;
  handleSelect: (studentId: string, inputKey: string) => void;
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  handleBlurInput: () => void;
  isSelected: boolean;
  isError?: boolean;
  isFakeFocus?: boolean;
  isInvitation?: boolean;
  isRosteringEnabled?: boolean;
};

const ButtonInputComponent = ({
  type,
  studentId,
  inputKey,
  value,
  handleSelect,
  handleInputChange,
  handleBlurInput,
  isSelected,
  isError,
  isFakeFocus,
  isInvitation,
  isRosteringEnabled,
}: ButtonInputComponentProps): JSX.Element => {
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const INPUT_MAX_LENGTH = 36;
    const PIN_CODE_MAX_LENGTH = 4;

    if (inputKey === 'pinCode' && e.target.value.length > PIN_CODE_MAX_LENGTH)
      return;

    if (e.target.value.length > INPUT_MAX_LENGTH) return;

    handleInputChange(inputKey, e.target.value);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputKey !== 'pinCode') return;
    if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault(); // Previene la entrada de caracteres no numéricos
    }
  };

  if (isInvitation) {
    return <>{isInvitation ? <SubtleText>{'-'}</SubtleText> : value}</>;
  }

  const isEditingDisabled =
    isRosteringEnabled &&
    (inputKey === 'firstName' ||
      inputKey === 'lastName' ||
      inputKey === 'email');

  if (isEditingDisabled) {
    return <>{value}</>;
  }

  return isSelected ? (
    <StyledInput
      type={type}
      value={value}
      onChange={handleOnChange}
      onKeyDown={handleOnKeyDown}
      onBlur={handleBlurInput}
      status={isError ? 'error' : undefined}
      className="table-input"
      autoFocus
    />
  ) : (
    <Button
      variant="tertiary"
      onClick={() => handleSelect(studentId, inputKey)}
      fill
      className={`table-button ${isFakeFocus ? 'fake-focus' : ''}`}
    >
      {value}
    </Button>
  );
};

const renderAvatar = ({
  avatar,
  id,
  firstName,
  handleOpenAvatarEditor,
}: {
  avatar?: string;
  id?: string;
  firstName?: string;
  handleOpenAvatarEditor: (id: string, img: string | null | undefined) => void;
}): JSX.Element | null => {
  const isTemplateStudent = id === TEMPLATE_ID;
  if (isTemplateStudent) return null;

  if (avatar) {
    return (
      <Avatar
        img={avatar}
        styles={{ margin: '0 auto' }}
        alt={`student-${firstName}`}
        onClick={() => handleOpenAvatarEditor(id!, avatar)}
      />
    );
  } else {
    return (
      <IconButton
        icon="AddPictureIcon"
        size="L"
        onClick={() => handleOpenAvatarEditor(id!, avatar)}
      />
    );
  }
};

const renderStudentName = ({
  id,
  firstName,
  lastName,
  alias,
  isInvitation,
  t,
  isHeader,
  selectedStudentId,
  selectedInputKey,
  isAnonymized,
  isRosteringEnabled,
  handleSelect,
  handleInputChange,
  handleBlurInput,
}: {
  id?: string;
  firstName?: string;
  lastName?: string;
  alias?: string;
  isInvitation?: boolean;
  isHeader?: boolean;
  selectedStudentId: string;
  selectedInputKey: string;
  isAnonymized?: boolean;
  isRosteringEnabled?: boolean;
  t: Translate;
  handleSelect: (studentId: string, inputKey: string) => void;
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  handleBlurInput: () => void;
}): JSX.Element => {
  const isNameSelected =
    selectedStudentId === id && selectedInputKey === 'firstName';
  const isLastNameSelected =
    selectedStudentId === id && selectedInputKey === 'lastName';
  const isAliasSelected =
    selectedStudentId === id && selectedInputKey === 'alias';

  const isTemplateStudent = id === TEMPLATE_ID;

  return (
    <SublabelFlexContainer>
      <StyledSpan width={isAnonymized ? columnWidth[33] : columnWidth[50]}>
        {isHeader ? (
          t('reports.name.title.column')
        ) : (
          <ButtonInputComponent
            handleInputChange={handleInputChange}
            inputKey="firstName"
            value={firstName || ''}
            studentId={id!}
            handleSelect={handleSelect}
            handleBlurInput={handleBlurInput}
            isSelected={isNameSelected}
            isError={firstName === ''}
            isInvitation={isInvitation}
            isRosteringEnabled={isRosteringEnabled}
          />
        )}
      </StyledSpan>
      <StyledSpan width={isAnonymized ? columnWidth[33] : columnWidth[50]}>
        {isHeader ? (
          t('reports.surname.title.column')
        ) : (
          <ButtonInputComponent
            handleInputChange={handleInputChange}
            inputKey="lastName"
            value={lastName || ''}
            studentId={id!}
            handleSelect={handleSelect}
            handleBlurInput={handleBlurInput}
            isSelected={isLastNameSelected}
            isError={lastName === '' || !lastName}
            isFakeFocus={isTemplateStudent}
            isInvitation={isInvitation}
            isRosteringEnabled={isRosteringEnabled}
          />
        )}
      </StyledSpan>

      {isAnonymized && (
        <StyledSpan width={columnWidth[33]}>
          {isHeader ? (
            t('reports.alias.title.column', 'Alias')
          ) : (
            <ButtonInputComponent
              handleInputChange={handleInputChange}
              inputKey="alias"
              value={alias?.toUpperCase() || ''}
              studentId={id!}
              handleSelect={handleSelect}
              handleBlurInput={handleBlurInput}
              isSelected={isAliasSelected}
              isInvitation={isInvitation}
              isRosteringEnabled={isRosteringEnabled}
            />
          )}
        </StyledSpan>
      )}
    </SublabelFlexContainer>
  );
};

const renderStudentNameSkeleton = (isAnonymized?: boolean): JSX.Element => {
  return (
    <FlexContainer>
      <StyledSpan width={isAnonymized ? columnWidth[33] : columnWidth[50]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={isAnonymized ? columnWidth[33] : columnWidth[50]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      {isAnonymized && (
        <StyledSpan width={columnWidth[33]}>
          <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
        </StyledSpan>
      )}
    </FlexContainer>
  );
};

const TooltipIcon = ({ t }: { t: Translate }): JSX.Element => {
  const theme = useGlimmerTheme();
  return (
    <Tooltip content={t('students.table.familyEmails.tooltip')}>
      <Icon
        size="M"
        icon="WarningIcon"
        iconColor={theme.tokens.color.alias.cm.icon['icon-inverted'].value}
        bgColor={theme.tokens.color.alias.cm.icon['icon-warning'].value}
      />
    </Tooltip>
  );
};

const renderHomeAccessSublabel = ({
  t,
  hasClassroomDigitalPracticeAccess,
  handleOpenDigitalAccessConfirmationModal,
  handleOpenDigitalPracticeAccessModal,
}: {
  t: Translate;
  hasClassroomDigitalPracticeAccess: boolean;
  handleOpenDigitalAccessConfirmationModal: () => void;
  handleOpenDigitalPracticeAccessModal: () => void;
}): JSX.Element => {
  return (
    <SublabelFlexContainer>
      <SublabelStyledSpan width={columnWidth[30]}>
        <DigitalPracticeSublabel>
          <DigitalPracticeSublabelContent
            hasClassroomDigitalPracticeAccess={
              hasClassroomDigitalPracticeAccess
            }
            handleOpenDigitalPracticeAccessModal={
              handleOpenDigitalPracticeAccessModal
            }
            handleOpenDigitalAccessConfirmationModal={
              handleOpenDigitalAccessConfirmationModal
            }
          />
        </DigitalPracticeSublabel>
      </SublabelStyledSpan>
      <SublabelStyledSpan
        width={columnWidth[30]}
        notVisible={!hasClassroomDigitalPracticeAccess}
      >
        <WeeklyTimeContainer>
          {t('students.table.labels.weeklyTime')}
          <div className="info-icon">
            <InfoColumnIcon tooltipText={t('students.home-access.time.info')} />
          </div>
        </WeeklyTimeContainer>
      </SublabelStyledSpan>
      <SublabelStyledSpan
        width={columnWidth[40]}
        notVisible={!hasClassroomDigitalPracticeAccess}
      >
        {t('students.table.labels.familyEmails')}
      </SublabelStyledSpan>
    </SublabelFlexContainer>
  );
};

const renderHomeAccess = ({
  t,
  id,
  homeAccess,
  weeklyTimeLimit,
  parents,
  individualAccess,
  handleInputChange,
  isInvitation,
  weeklyTimeLimitOptions,
  hasClassroomDigitalPracticeAccess,
  classroomHasHomeAccess,
  handleToggleHomeAccess,
  handleInviteParents,
}: {
  t: Translate;
  id?: string;
  homeAccess?: boolean;
  weeklyTimeLimit?: number;
  parents?: Parent[];
  individualAccess?: boolean;
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  isInvitation?: boolean;
  weeklyTimeLimitOptions: Option[];
  hasClassroomDigitalPracticeAccess: boolean;
  classroomHasHomeAccess: boolean;
  handleToggleHomeAccess: (value: boolean, studentId: string) => void;
  handleInviteParents: (id: string) => void;
}): JSX.Element => {
  const weeklyTimeLimitLabel = weeklyTimeLimitOptions?.find(
    (option) => option.value === weeklyTimeLimit
  )?.label;

  const label = weeklyTimeLimitLabel;

  const toggleIsActive = hasClassroomDigitalPracticeAccess
    ? classroomHasHomeAccess
      ? homeAccess
      : false
    : false;

  const isTemplateStudent = id === TEMPLATE_ID;

  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[30]} notVisible={isTemplateStudent}>
        <ToggleButton
          disabled={!hasClassroomDigitalPracticeAccess}
          isActive={toggleIsActive}
          style={{ alignSelf: 'center' }}
          onToggle={(value) => handleToggleHomeAccess(value, id!)}
        />
      </StyledSpan>
      <StyledSpan
        width={columnWidth[30]}
        notVisible={!hasClassroomDigitalPracticeAccess || isTemplateStudent}
      >
        <DropDownComponent
          isDisabled={!toggleIsActive}
          label={label || ''}
          options={weeklyTimeLimitOptions}
          handleInputChange={handleInputChange}
          studentId={id!}
          inputKey={'weeklyTimeLimit'}
        />
      </StyledSpan>

      <StyledSpan
        width={columnWidth[40]}
        notVisible={!hasClassroomDigitalPracticeAccess || isTemplateStudent}
      >
        <StyledSpan notVisible={isTemplateStudent}>
          {!parents || parents.length === 0 ? (
            <>
              <Button
                className="table-button"
                disabled={!toggleIsActive}
                size={'M'}
                variant={'tertiary'}
                oneLine
                onClick={() => handleInviteParents(id!)}
                rightIcon={'AddUpIcon'}
              >
                {t('students.table.addEmails')}
              </Button>
              {parents?.length === 0 && homeAccess && !individualAccess && (
                <TooltipIcon t={t} />
              )}
            </>
          ) : (
            <ParentsStatusCell
              parents={parents}
              disabled={isInvitation || !homeAccess}
              onClick={() => handleInviteParents(id!)}
            />
          )}
        </StyledSpan>
      </StyledSpan>
    </FlexContainer>
  );
};

const renderHomeAccessSkeleton = (
  hasClassroomDigitalPracticeAccess: boolean
): JSX.Element => {
  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[30]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      {hasClassroomDigitalPracticeAccess && (
        <>
          <StyledSpan width={columnWidth[30]}>
            <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
          </StyledSpan>
          <StyledSpan width={columnWidth[40]}>
            <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
          </StyledSpan>
        </>
      )}
    </FlexContainer>
  );
};

const renderDigitalPracticeSubLabel = ({
  t,
  stage,
  classroomCapabilities,
}: {
  t: Translate;
  stage: string | null;
  classroomCapabilities?: ClassroomCapabilities;
}): JSX.Element => {
  const canPracticeLevelBeChanged =
    classroomCapabilities?.practice?.canLevelBeChanged ?? true;

  if (stage === Stage.INFANTIL)
    return (
      <SublabelFlexContainer>
        <SublabelStyledSpan width={columnWidth[50]}>
          {t('students.table.labels.educationalPlan')}
        </SublabelStyledSpan>
        <SublabelStyledSpan width={columnWidth[50]}>
          {t('students.table.labels.language')}
        </SublabelStyledSpan>
      </SublabelFlexContainer>
    );
  else if (stage === Stage.PRIMARIA)
    return (
      <SublabelFlexContainer>
        {canPracticeLevelBeChanged && (
          <SublabelStyledSpan width={columnWidth[20]}>
            {t('students.table.labels.educationalPlan')}
          </SublabelStyledSpan>
        )}
        <SublabelStyledSpan width={columnWidth[20]}>
          {t('students.table.labels.language')}
        </SublabelStyledSpan>
        <SublabelStyledSpan width={columnWidth[20]}>
          {t('students.table.labels.access-pin')}
        </SublabelStyledSpan>
        <SublabelStyledSpan width={columnWidth[40]}>
          {t('students.table.labels.studentEmail')}
        </SublabelStyledSpan>
      </SublabelFlexContainer>
    );
  else if (stage === Stage.SECUNDARIA)
    return (
      <SublabelFlexContainer>
        <SublabelStyledSpan width={columnWidth[40]}>
          {t('Settings.Email')}
        </SublabelStyledSpan>
        <SublabelStyledSpan width={columnWidth[15]}>
          {t('students.table.labels.educationalPlan')}
        </SublabelStyledSpan>
        <SublabelStyledSpan width={columnWidth[15]}>
          {t('students.table.labels.language')}
        </SublabelStyledSpan>
        <SublabelStyledSpan width={columnWidth[20]}>
          {t('common.status')}
        </SublabelStyledSpan>
      </SublabelFlexContainer>
    );
  return <></>;
};

const renderPrimaryDigitalPractice = ({
  t,
  pinCode,
  language,
  email,
  practiceLevelId,
  id,
  isInvitation,
  practiceLevelOptions,
  handleInputChange,
  availableLanguages,
  individualAccess,
  handleSelect,
  selectedStudentId,
  selectedInputKey,
  handleBlurInput,
  handleToggleIndividualAccess,
  isRosteringEnabled,
  classroomCapabilities,
}: {
  t: Translate;
  pinCode?: number;
  language?: string;
  email?: string;
  practiceLevelId?: string;
  isInvitation?: boolean;
  individualAccess?: boolean;
  id?: string;
  practiceLevelOptions: Option[];
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  availableLanguages: Option[];
  selectedStudentId: string;
  selectedInputKey: string;
  handleSelect: (studentId: string, inputKey: string) => void;
  handleBlurInput: () => void;
  handleToggleIndividualAccess: (value: boolean, id: string) => void;
  stage: string | null;
  isRosteringEnabled: boolean;
  classroomCapabilities?: ClassroomCapabilities;
}): JSX.Element => {
  const practiceLevel = getPracticeLevel(practiceLevelId, practiceLevelOptions);
  const practiceLevelLabel = practiceLevelOptions.find(
    (option) => option.value === practiceLevel
  )?.label;

  const languageLabel = availableLanguages.find(
    (option) => option.value === language
  )?.label;

  const isTemplateStudent = id === TEMPLATE_ID;
  const canPracticeLevelBeChanged =
    classroomCapabilities?.practice?.canLevelBeChanged ?? true;

  return (
    <FlexContainer>
      {canPracticeLevelBeChanged && (
        <StyledSpan width={columnWidth[20]} notVisible={isTemplateStudent}>
          <DropDownComponent
            isDisabled={isInvitation}
            label={practiceLevelLabel || ''}
            options={practiceLevelOptions}
            handleInputChange={handleInputChange}
            studentId={id!}
            inputKey="bubblePlanId"
          />
        </StyledSpan>
      )}
      <StyledSpan width={columnWidth[20]} notVisible={isTemplateStudent}>
        <DropDownComponent
          isDisabled={isInvitation}
          label={languageLabel || ''}
          options={availableLanguages}
          handleInputChange={handleInputChange}
          studentId={id!}
          inputKey="language"
        />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]} notVisible={isTemplateStudent}>
        <ButtonInputComponent
          type="number"
          handleInputChange={handleInputChange}
          inputKey="pinCode"
          value={pinCode || ''}
          studentId={id!}
          handleSelect={handleSelect}
          handleBlurInput={handleBlurInput}
          isSelected={
            selectedStudentId === id && selectedInputKey === 'pinCode'
          }
        />
      </StyledSpan>
      <StyledSpan width={columnWidth[40]} notVisible={isTemplateStudent}>
        {!isRosteringEnabled && (
          <ToggleButton
            isActive={individualAccess}
            onToggle={(value) => handleToggleIndividualAccess(value, id!)}
          />
        )}

        {individualAccess ? (
          <StyledTooltip content={email}>
            <EmailField>{email}</EmailField>
          </StyledTooltip>
        ) : (
          ''
        )}
      </StyledSpan>
    </FlexContainer>
  );
};

const renderEyDigitalPractice = ({
  language,
  practiceLevelId,
  id,
  isInvitation,
  practiceLevelOptions,
  handleInputChange,
  availableLanguages,
}: {
  language?: string;
  email?: string;
  practiceLevelId?: string;
  isInvitation?: boolean;
  id?: string;
  practiceLevelOptions: Option[];
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  availableLanguages: Option[];
  selectedStudentId: string;
  selectedInputKey: string;
  handleSelect: (studentId: string, inputKey: string) => void;
  handleBlurInput: () => void;
  weeklyTimeLimitOptions: Option[];
}): JSX.Element => {
  const practiceLevel = getPracticeLevel(practiceLevelId, practiceLevelOptions);
  const practiceLevelLabel = practiceLevelOptions.find(
    (option) => option.value === practiceLevel
  )?.label;

  const languageLabel = availableLanguages.find(
    (option) => option.value === language
  )?.label;

  const isTemplateStudent = id === TEMPLATE_ID;

  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[50]} notVisible={isTemplateStudent}>
        <DropDownComponent
          isDisabled={isInvitation}
          label={practiceLevelLabel || ''}
          options={practiceLevelOptions}
          handleInputChange={handleInputChange}
          studentId={id!}
          inputKey="bubblePlanId"
        />
      </StyledSpan>
      <StyledSpan width={columnWidth[50]} notVisible={isTemplateStudent}>
        <DropDownComponent
          isDisabled={isInvitation}
          label={languageLabel || ''}
          options={availableLanguages}
          handleInputChange={handleInputChange}
          studentId={id!}
          inputKey="language"
        />
      </StyledSpan>
    </FlexContainer>
  );
};

const renderSecondaryDigitalPractice = ({
  t,
  language,
  email,
  practiceLevelId,
  lastLogin,
  id,
  isInvitation,
  practiceLevelOptions,
  handleInputChange,
  availableLanguages,
}: {
  t: Translate;
  language?: string;
  email?: string;
  parents?: Parent[];
  weeklyTimeLimit?: number;
  practiceLevelId?: string;
  isInvitation?: boolean;
  lastLogin?: string;
  id?: string;
  practiceLevelOptions: Option[];
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  availableLanguages: Option[];
  selectedStudentId: string;
  selectedInputKey: string;
}): JSX.Element => {
  const practiceLevel = getPracticeLevel(practiceLevelId, practiceLevelOptions);
  const practiceLevelLabel = practiceLevelOptions.find(
    (option) => option.value === practiceLevel
  )?.label;

  const languageLabel = availableLanguages.find(
    (option) => option.value === language
  )?.label;

  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[40]}>
        <EmailField onClick={() => navigator.clipboard.writeText(email!)}>
          {isInvitation ? <SubtleText>{email}</SubtleText> : email}
        </EmailField>
      </StyledSpan>
      <StyledSpan width={columnWidth[15]} notVisible={isInvitation}>
        <DropDownComponent
          isDisabled={isInvitation}
          label={practiceLevelLabel || ''}
          options={practiceLevelOptions}
          handleInputChange={handleInputChange}
          studentId={id!}
          inputKey="educationalPlanId"
        />
      </StyledSpan>
      <StyledSpan width={columnWidth[15]} notVisible={isInvitation}>
        <DropDownComponent
          isDisabled={isInvitation}
          label={languageLabel || ''}
          options={availableLanguages}
          handleInputChange={handleInputChange}
          studentId={id!}
          inputKey="language"
        />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <StudentStatusCell isInvitation={isInvitation} lastLogin={lastLogin} />
      </StyledSpan>
    </FlexContainer>
  );
};

const renderEyDigitalPracticeSkeleton = (): JSX.Element => {
  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
    </FlexContainer>
  );
};

const renderPrimaryDigitalPracticeSkeleton = (): JSX.Element => {
  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[40]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
    </FlexContainer>
  );
};

const renderSecondaryDigitalPracticeSkeleton = (): JSX.Element => {
  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[40]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[15]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[15]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
      <StyledSpan width={columnWidth[20]}>
        <Skeleton width={'100%'} height={'24px'} radius={'4px'} />
      </StyledSpan>
    </FlexContainer>
  );
};

const getPracticeLevel = (
  practiceLevelId: string | null | undefined,
  practiceLevelOptions: Option[]
): string | number | undefined => {
  if (practiceLevelId !== null) return practiceLevelId;

  if (practiceLevelOptions?.length > 0)
    return practiceLevelOptions[practiceLevelOptions?.length - 1].value;

  return;
};

const renderInterventionSubLabel = ({ t }: { t: Translate }): JSX.Element => {
  return (
    <SublabelFlexContainer>
      <SublabelStyledSpan width={columnWidth[25]}>
        {t('students.table.label.toggleRti')}
      </SublabelStyledSpan>
      <SublabelStyledSpan width={columnWidth[30]}>
        {t('students.table.labels.rtiStartDate')}
      </SublabelStyledSpan>
      <SublabelStyledSpan width={columnWidth[30]}>
        {t('students.table.labels.rtiEndDate')}
      </SublabelStyledSpan>
    </SublabelFlexContainer>
  );
};

const renderIntervention = ({
  t,
  handleToggleRti,
  id,
  rti,
}: {
  t: Translate;
  handleToggleRti: (studentId: string, value: boolean) => void;
  id?: string;
  rti?: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
}): JSX.Element => {
  return (
    <FlexContainer>
      <StyledSpan width={columnWidth[25]}>
        <ToggleButton
          isActive={rti?.enabled}
          onToggle={(value) => handleToggleRti(id!, value)}
        />
      </StyledSpan>
      <StyledSpan width={columnWidth[30]}>
        {dates.getFormattedDate(rti?.startDate) || '-'}
      </StyledSpan>
      <StyledSpan width={columnWidth[30]}>
        {dates.getFormattedDate(rti?.endDate) || '-'}
      </StyledSpan>
    </FlexContainer>
  );
};

export {
  renderAvatar,
  renderDigitalPracticeSubLabel,
  renderEyDigitalPractice,
  renderEyDigitalPracticeSkeleton,
  renderHomeAccess,
  renderHomeAccessSkeleton,
  renderHomeAccessSublabel,
  renderIntervention,
  renderInterventionSubLabel,
  renderPrimaryDigitalPractice,
  renderPrimaryDigitalPracticeSkeleton,
  renderSecondaryDigitalPractice,
  renderSecondaryDigitalPracticeSkeleton,
  renderStudentName,
  renderStudentNameSkeleton,
};
